import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Map as MapLibreMap } from 'maplibre-gl';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, Fab, IconButton, MenuItem, Paper, Snackbar, Stack, TextField,
  Tooltip, Typography,
} from '@mui/material';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';

import { useLocation, useNavigate } from 'react-router-dom';

import { api, ApiError, basemapUrlFor } from '../api/client';
import { TripForm } from '../components/TripForm';
import { TripDayStrip } from '../components/TripDayStrip';
import { ItineraryPanel } from '../components/ItineraryPanel';
import { PinnedStopDialog } from '../components/PinnedStopDialog';
import { PlanningProgress } from '../components/PlanningProgress';
import { pickDefaultCity, rememberCity } from '../util/defaultCity';
import type { ViewBox } from '../util/viewBox';
import { covers, wider } from '../util/viewBox';
import { PlaceDetailsDialog } from '../components/PlaceDetailsDialog';
import { PlacePickerDialog, type PickedPoi } from '../components/PlacePickerDialog';
import { MapView } from '../components/MapView';
import { MapOnlyToggle } from '../components/MapOnlyToggle';
import { ExportRouteButton } from '../components/ExportRouteButton';
import { dayPlanRequest, directionsForPlan } from '../export/dayRequest';
import { AssistantPanel } from '../components/AssistantPanel';
import { FEATURES } from '../config/features';
import { CategoryPanel } from '../components/CategoryPanel';
import { buildCategoryColours, colourForDay } from '../theme';
import { spanTitle } from '../util/routeTitle';
import type {
  Category, City, CoordStop, PlanRequest, Poi, RoutePlan, RouteStyle, TripPlan, TripRequest,
} from '../api/types';

/**
 * Several days, planned together.
 *
 * Every day here is one ordinary plan, produced by the same pipeline the
 * single-day screen uses — so this page is a way of moving between days and
 * nothing more. It owns the trip; each day's contents belong to the same
 * components that render a single route, unchanged.
 */


/** "Day 2 - Sat 10 Oct", or just the number where there is no date. */
function dayLabel(index: number, date: string | null): string {
  const named = date
    ? new Date(`${date}T00:00:00`).toLocaleDateString(undefined, {
      weekday: 'short', day: 'numeric', month: 'short',
    })
    : '';

  return named ? `Day ${index + 1} \u2014 ${named}` : `Day ${index + 1}`;
}

export function TripPage() {
  const [cities, setCities] = useState<City[] | null>(null);
  const [city, setCity] = useState<City | null>(null);
  const [styles, setStyles] = useState<RouteStyle[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [settings, setSettings] = useState<Record<string, unknown>>({});

  /*
   * The live map, held only so the export can take a picture of it. A ref
   * rather than state: nothing re-renders because the map finished loading.
   */
  const exportMap = useRef<MapLibreMap | null>(null);
  const [request, setRequest] = useState<TripRequest | null>(null);
  const [trip, setTrip] = useState<TripPlan | null>(null);
  /*
   * Whether a trip exists, readable within the tick that created one.
   *
   * A single turn can produce both a trip and the day it was built from, and
   * the panel reports them one after the other: the trip first, then the
   * day. React batches both, so the day's handler still saw `trip` as null
   * and would have decided this screen had nothing to attach a day to — one
   * line after being given a week. Written where the trip is, so the second
   * handler sees what the first did.
   */
  const hasTrip = useRef(false);
  useEffect(() => { hasTrip.current = trip !== null; }, [trip]);
  /*
   * A trip for the form to OPEN on, which is not the same as the current one.
   *
   * `request` is rewritten by every edit, and the form seeds itself from
   * whatever it is handed — so passing the live request would take the form
   * back off whoever was editing it, field by field, as they typed. This is
   * set only when a trip arrives from somewhere else: handed over from the
   * single-day screen, or built by the assistant.
   */
  const [formSeed, setFormSeed] = useState<TripRequest | null>(null);

  /*
   * A trip the assistant planned on the other screen.
   *
   * It arrives in the router's own state because switching planner is a
   * navigation, not a prop: somebody asked for three days while looking at a
   * day, and being shown the multi-day screen already holding those three
   * days is the answer to that. Cleared from history immediately, so a later
   * reload does not resurrect a trip the traveller has moved on from.
   */
  const location = useLocation();
  const navigate = useNavigate();
  const [selected, setSelected] = useState(0);
  const [planning, setPlanning] = useState(false);
  /** The day being re-planned on its own, if any. */
  const [busyDay, setBusyDay] = useState<number | null>(null);
  /** Days whose inherited endpoint changed under them. */
  const [stale, setStale] = useState<number[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [detailsPoiId, setDetailsPoiId] = useState<string | null>(null);

  /** A place clicked on the map, waiting to be given a role on this trip. */
  const [picked, setPicked] = useState<PickedPoi | null>(null);
  /*
    A spot marked by right-click, waiting to be told what it is.

    The same gesture the one-day planner has, which was switched off here on
    the grounds that a trip has no single day for a dropped pin to belong to.
    It does: the map shows one day at a time, and clicking a PLACE has long
    assigned it to that day. A marked spot now follows the same rule, so the
    gesture that does nothing is no longer the answer to "which day?".
  */
  const [markedPoint, setMarkedPoint] = useState<{ lat: number; lng: number } | null>(null);
  /*
    Whether a marked spot is the operation's business or one day's.

    The form asks everything twice over — once for the whole operation, once
    per day — and the map had no way to say which was meant, so each role
    picked a scope for itself: a stop went to the day, an avoided area to the
    operation. Asked outright instead, and reset to the day each time the
    dialog opens, since the day is what the map is showing.
  */
  /*
    Starts on the operation, because that is what is being built.

    Before anything is planned there are no days to speak of — only the base,
    where the days finish and the ground they all keep clear of, every one of
    which is held in common. Opening on Day 1 would have had the first thing
    anybody marked land on one day of an operation that does not exist yet.
    The first plan moves it to the day on screen, which is the thing being
    adjusted from then on.
  */
  const [markScope, setMarkScope] = useState<'operation' | 'day'>('operation');
  const [toast, setToast] = useState<string | null>(null);
  /** Which stop of the selected day is picked out, on the map and in the list. */
  const [highlighted, setHighlighted] = useState<number | null>(null);

  /*
   * Which stop of the day on screen the pinned-stop panel is describing.
   *
   * Held as an index into the current day rather than the stop itself, so
   * moving to another day or re-planning cannot leave the panel showing times
   * that day no longer keeps.
   */
  const [pinIndex, setPinIndex] = useState<number | null>(null);
  /*
   * Places a suggestion panel is offering, and which one the cursor is on.
   * Drawn hollow so they cannot be mistaken for the day as planned.
   */
  const [suggestionPreview, setSuggestionPreview] = useState<{
    places: { poiId: string; name: string; lat: number; lng: number }[];
    hoveredPoiId: string | null;
  }>({ places: [], hoveredPoiId: null });
  /** The search corridor, while the traveller has asked to see it. */
  const [coverage, setCoverage] = useState<unknown | null>(null);
  /*
   * Avoidance is trip-wide and lives here rather than in the form, exactly as
   * it does on the single-day screen: the map draws the exclusion zones before
   * anything is planned, and the map is the page's.
   */
  /*
   * Where the trip is based, and the places it must include.
   *
   * Lifted out of the form for the same reason the single-day screen lifts
   * its endpoints: the map draws them, and the map belongs to the page.
   */
  const [base, setBase] = useState<Poi | null>(null);
  /*
   * Where a day is meant to finish, when that is not the base it left from.
   * Optional: left empty, every day returns to base, which is the based case
   * and what the trip screen has always assumed.
   */
  const [finish, setFinish] = useState<Poi | null>(null);
  /*
    The finish once "return to the base" has had its say — null when every
    day comes home. Reported by the form, because the toggle lives there and
    `finish` keeps its value while it is off.
  */
  const [objective, setObjective] = useState<Poi | null>(null);

  /*
    Spots marked on the map before anything has been planned.

    Until a plan exists there is no request to write into, and every list the
    page already keeps holds PLACES — records with an id — so a bare
    coordinate had nowhere to go and the map could only be marked after the
    fact. These are the same four lists the one-day screen keeps for exactly
    this, and the form folds them into the request it builds.

    Stops are held per day, because that is how they are sent; the rest are
    the operation's.
  */
  const [basePin, setBasePin] = useState<CoordStop | null>(null);
  const [finishPin, setFinishPin] = useState<CoordStop | null>(null);
  /* The marked finish once "return to the base" has had its say. */
  const [objectivePin, setObjectivePin] = useState<CoordStop | null>(null);
  const [dayStopPins, setDayStopPins] = useState<Record<number, CoordStop[]>>({});
  /* A day's own start and finish, marked on the map rather than chosen. */
  const [dayEndPins, setDayEndPins] =
    useState<Record<number, { start?: CoordStop; end?: CoordStop }>>({});
  const [avoidPointPins, setAvoidPointPins] = useState<CoordStop[]>([]);
  /* Whether the days come home, reported by the form — see `hasBothEnds`. */
  const [returnToBase, setReturnToBase] = useState(true);
  /* The days the form is holding, so the strip can be shown before a plan. */
  const [draftDays, setDraftDays] = useState<{
    date: string;
    skip: boolean;
    /* This day's OWN ends, where it has them, as the form resolves them. */
    start: CoordStop | null;
    end: CoordStop | null;
    signature: string;
  }[]>([]);
  /*
    What each day was planned from, so an edit made in the form shows.

    A day changed on the MAP re-plans at once and says so. A day changed in
    the form does neither — it waits for the plan button, which is right,
    but it waited silently: no toast, no badge, nothing to say the day on
    screen no longer matched the form beside it. Comparing against this marks
    it out of date, which is the same thing the strip already says when an
    earlier day moves an endpoint a later one inherited.
  */
  const daysPlannedFrom = useRef<string[]>([]);

  /*
    The days the strip shows: the planned ones once there are any, else the
    ones the form is holding.

    The strip used to appear only after planning, which made marking the map
    a thing you could do only to a finished operation — exactly backwards,
    since choosing stops and areas on the map is how you would rather build
    one in the first place. An unplanned day carries no plan, so the card
    shows its date and says it is not planned yet.
  */
  /*
    Whether the operation has somewhere to set off from and somewhere to end.

    A stop is somewhere the route passes on its way between the two, and an
    exclusion is ground it holds clear of on the way — so before both are
    settled there is no way, and nothing for either to be about. The same
    reasoning the one-day screen gives for the same two buttons.

    With "every day returns to the base" on, the base IS the finish, so a
    base alone settles it.
  */
  const hasBothEnds = Boolean(base || basePin)
    && (returnToBase || Boolean(objective || objectivePin));

  /*
    Whether any day's card has moved on from the day that was planned.

    The prompt over the map was driven by the OPERATION's signature alone —
    its base, finish, places and avoidance — so everything asked of a single
    day went unnoticed: its own exclusions, its sites, its breaks, its
    transport. The change was made, the map went on showing the old round,
    and nothing anywhere said to plan again.

    Not the strip's "Out of date" badge, which belongs to a different case:
    a day whose endpoint an earlier day moved, marked rather than re-planned.
    This is "what you typed is not on the map", and it is answered the way
    the one-day screen answers it — one prompt, with the button on it.
  */
  const daysMoved = useMemo(
    () => Boolean(trip)
      && daysPlannedFrom.current.length > 0
      && draftDays.some((d, i) => daysPlannedFrom.current[i] !== undefined
        && daysPlannedFrom.current[i] !== d.signature),
    [draftDays, trip],
  );

  /*
    What a mark actually applies to.

    Held in common only while the operation is being built. Once it is
    planned there are real days on the map, each with its own route, and
    "every day" stops being a thing anybody is looking at — a change at that
    scope would quietly invalidate all of them from a card sitting over one.
    The card goes with it, so the strip is days and nothing else, and the
    operation's own avoidance stays where it is edited: the form.

    Derived rather than only set when a plan lands, so a scope left over from
    before can never disagree with a strip that no longer offers it.
  */
  const scope = trip ? 'day' : markScope;

  const stripDays = useMemo(
    () => trip?.days ?? draftDays.map((d, index) => ({
      index, date: d.date, skipped: d.skip,
    })),
    [trip, draftDays],
  );
  const [mustVisit, setMustVisit] = useState<Poi[]>([]);
  const [avoid, setAvoid] = useState<Poi[]>([]);
  const [avoidCategories, setAvoidCategories] = useState<string[]>([]);
  const [avoidDistanceM, setAvoidDistanceM] = useState(500);

  /*
   * The parts of an operation the MAP can change, as one string.
   *
   * The form already makes you press a button, so a trip is never planned
   * behind your back. The map is the gap: marking a base, dropping a stop,
   * excluding an area from the right-click menu all change the operation
   * while the planned one stays drawn, and nothing said so.
   *
   * Only what the map can reach is in here. Everything else goes through the
   * form, which re-sends it on the next plan whatever this says.
   */
  const mapSignature = useMemo(() => JSON.stringify({
    base: base?.id ?? null,
    finish: finish?.id ?? null,
    mustVisit: mustVisit.map((p) => p.id),
    avoid: avoid.map((p) => p.id),
    avoidCategories,
    avoidDistanceM,
  }), [base, finish, mustVisit, avoid, avoidCategories, avoidDistanceM]);

  // What the drawn operation was planned from, and the counter that asks the
  // form to plan again.
  const plannedFrom = useRef<string | null>(null);
  const [replanSignal, setReplanSignal] = useState(0);

  const staleTrip = Boolean(trip)
    && plannedFrom.current !== null
    && plannedFrom.current !== mapSignature;
  /*
   * Per-place and per-category radii. One distance rarely suits everything
   * avoided — a nightclub might want 200 m and a motorway junction 1 km — so
   * the slider stays the default and these record what was given its own.
   */
  const [avoidDistanceByPoiId, setAvoidDistanceByPoiId] = useState<Record<string, number>>({});
  const [avoidDistanceByCategoryKey, setAvoidDistanceByCategoryKey] =
    useState<Record<string, number>>({});
  /** Coordinates of places in the avoided categories, for drawing their zones. */
  const [avoidCategoryPoints, setAvoidCategoryPoints] = useState<
    { lat: number; lng: number; categoryKey: string }[]
  >([]);

  /** Categories being browsed on the map, as on the planner. */
  const [shownCategories, setShownCategories] = useState<string[]>([]);
  const [showCategories, setShowCategories] = useState(false);
  const [poiData, setPoiData] = useState<unknown>(null);
  /*
    The ground the dots have been fetched for.

    Grown beyond the view, so a short pan stays inside what has already
    been read and costs nothing; replaced only when the map shows ground
    this box does not hold, which is what zooming out or travelling does.
  */
  const [poiBox, setPoiBox] = useState<ViewBox | null>(null);
  const watchView = useCallback((bounds: ViewBox) => {
    setPoiBox((current) => (current && covers(current, bounds) ? current : wider(bounds)));
  }, []);


  /**
   * Whether the form is open, kept separate from whether a trip exists.
   *
   * The same distinction the single-day screen draws: the plan stays on the
   * map while it is being edited, so a traveller is adjusting against the
   * thing they can see rather than against a blank.
   */
  /*
   * The assistant, on the multi-day screen as it is on the single-day one.
   *
   * It was only ever mounted on the planner, which made "plan by asking" a
   * thing you could do for a day and not for a week — the harder of the two
   * to assemble by hand, and the one where saying it in a sentence saves the
   * most clicking. The component is the same; what differs is that a day is
   * chosen here, so it is handed the selected day's request and plan.
   */
  const [assistantOpen, setAssistantOpen] = useState(false);
  /*
   * Whether the form and itinerary are showing beside the map.
   *
   * The single-day screen has had this since the assistant did: talking to it
   * about a route you cannot see is the one thing the panel makes worse, and
   * its header carries the control that folds the column away. This screen
   * mounted the same panel without the pair of props that draw that control,
   * so the button was simply absent — a gap in the header where the other
   * screen has a button.
   */
  const [panelOpen, setPanelOpen] = useState(true);
  /*
   * Which of the two a narrow screen is showing.
   *
   * On a phone the column and the map cannot share the width, so they take
   * turns — as they do on the single-day screen and on Saved. This screen
   * had no such turn to take: the column filled the width and the map was
   * squeezed beneath it, with nothing anywhere to swap them.
   *
   * `panelOpen` is the wide-screen answer and is left alone; below the
   * breakpoint there is nothing to fold away from, only something to swap to.
   */
  const [panelOnMobile, setPanelOnMobile] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState('');
  /*
   * A stop picked in the list, for the map to go to.
   *
   * The nonce is what makes picking the same stop twice move the map again;
   * without it the second click is a no-op, which reads as the list having
   * stopped working. Same shape the planner uses.
   */
  const [focusStop, setFocusStop] = useState<{ index: number; nonce: number } | null>(null);

  /*
   * Whether the places the round only passes are put down.
   *
   * Held here rather than in the map because the schedule hides them in
   * step: one switch, two panels, so the list and the line always agree
   * about what is being shown. Starts put down, and only means anything
   * where there are enough of them to be in the way.
   */
  const [passedHidden, setPassedHidden] = useState(true);

  const focusOnMap = (index: number) => {
    setHighlighted(index);
    setFocusStop((current) => ({ index, nonce: (current?.nonce ?? 0) + 1 }));
  };

  const [savedId, setSavedId] = useState<string | null>(null);
  /*
   * Whether this operation was OPENED from Saved, rather than planned here.
   *
   * Not the same as having a savedId — pressing Save on a trip planned a
   * moment ago gives it one too, and that trip is still the one being worked
   * on. This is only about arriving at a finished operation to read it, which
   * is what the single-day screen does with a saved route: it shows it, and
   * offers no way in.
   */
  const [openedFromSaved, setOpenedFromSaved] = useState(false);

  /*
   * Tracked apart from the settings themselves: `{}` is a valid answer, so it
   * cannot distinguish "no settings" from "not asked yet" — and choosing the
   * city on the cities response alone would take the fallback every time and
   * then never reconsider.
   */
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  /*
    Whether this deployment wants the planning assistant at all.

    `FEATURES.planningAgent` says whether it is in the build; this says whether
    the operator has switched it on. Unset is off, as Admin has always read it.
  */
  const assistantEnabled = settings['ai.enabled'] === true;

  /**
   * A trip request, read back into the form that would have produced it.
   *
   * Setting `request` and `trip` alone drew the days and left every picker
   * empty — the base, the finish, the places it must include and the ones it
   * keeps clear of all sat blank beside a planned week, so the first edit
   * from the form would have re-planned it without any of them. The same job
   * the single-day screen does when it adopts an assistant's answer: put
   * everything back where a traveller would have typed it.
   *
   * Place ids are resolved because the pickers show names; one that cannot be
   * found is dropped rather than left as an id nobody can read.
   */
  const adoptTripRequest = useCallback(async (next: TripRequest) => {
    const place = async (id: string) => api.poi(id).catch(() => null);
    const endpoint = async (ref: TripRequest['defaults']['start'] | undefined) => (
      /*
       * Only a place, because these two pickers hold places. The single-day
       * screen can pin a bare coordinate on the map; this screen has no such
       * pin, so a coordinate endpoint leaves the picker empty rather than
       * showing a place that was never chosen. The trip still plans from it —
       * it is in the request, which is what the server reads.
       */
      ref && ref.kind === 'poi' ? place(ref.poiId) : null
    );

    const [start, end] = await Promise.all([
      endpoint(next.defaults.start), endpoint(next.defaults.end),
    ]);
    setBase(start);
    setFinish(end);

    /*
     * The trip's own must-visits ride on the first day that is not skipped —
     * which is where the form puts them, so that is where they are read back
     * from. A later day's are the day's own and stay there.
     */
    const firstPlanned = next.days.find((d) => !d.skip);
    const wanted = firstPlanned?.mustVisitPoiIds ?? [];
    const found = await Promise.all(wanted.map(place));
    setMustVisit(found.filter((poi): poi is Poi => poi !== null));

    const avoidance = next.defaults.avoid;
    const avoided = await Promise.all((avoidance?.poiIds ?? []).map(place));
    setAvoid(avoided.filter((poi): poi is Poi => poi !== null));
    setAvoidCategories(avoidance?.categoryKeys ?? []);
    if (avoidance?.distanceM) setAvoidDistanceM(avoidance.distanceM);
    setAvoidDistanceByPoiId(avoidance?.distanceByPoiId ?? {});
    setAvoidDistanceByCategoryKey(avoidance?.distanceByCategoryKey ?? {});
  }, []);

  /*
   * A trip the assistant planned on the other screen.
   *
   * It arrives in the router's own state because switching planner is a
   * navigation, not a prop: somebody asked for three days while looking at a
   * day, and being shown the multi-day screen already holding those three
   * days is the answer to that. Cleared from history immediately, so a later
   * reload does not resurrect a trip the traveller has moved on from.
   *
   * The conversation comes too. Asking for a week and watching the chat that
   * was asked close itself made the switch look like a failure — the panel
   * is the same component either side, and it resumes the same conversation,
   * so all that has to cross is that it was open.
   */
  useEffect(() => {
    const handed = location.state as {
      tripRequest?: TripRequest; trip?: TripPlan; assistantOpen?: boolean;
      savedId?: string; savedName?: string;
    } | null;
    if (!handed?.trip || !handed.tripRequest) return;
    setRequest(handed.tripRequest);
    setTrip(handed.trip);
    setFormSeed(handed.tripRequest);
    hasTrip.current = true;
    /*
     * An operation opened from Saved stays that operation — so Save updates
     * it rather than making another, and the button says which it will do.
     */
    setSavedId(handed.savedId ?? null);
    setOpenedFromSaved(Boolean(handed.savedId));
    if (handed.savedName) setSaveName(handed.savedName);
    setSelected(0);
    setMarkScope('day');
    if (handed.assistantOpen) setAssistantOpen(true);
    void adoptTripRequest(handed.tripRequest);
    void navigate('.', { replace: true, state: null });
  }, [location.state, navigate, adoptTripRequest]);

  useEffect(() => {
    api.cities().then(setCities).catch((err: ApiError) => setError(err.message));
    api.settings()
      .then(setSettings)
      .catch(() => setSettings({}))
      .finally(() => setSettingsLoaded(true));
  }, []);

  /* The same rule the other screens use, so all three open on one city. */
  useEffect(() => {
    if (city || !cities?.length || !settingsLoaded) return;
    setCity(pickDefaultCity(cities, settings));
  }, [city, cities, settings, settingsLoaded]);

  /* So the next screen opens on it, rather than on the configured default. */
  useEffect(() => { rememberCity(city); }, [city]);

  /*
   * The city this page last held, so a CHANGE can be told from arriving at one.
   *
   * The clear below is right when somebody picks a different city and wrong
   * on the first load, and the effect could not tell the two apart: `city`
   * starts null and is filled once the cities and settings arrive, which
   * looked like a change and wiped the page. A trip handed over from the
   * single-day screen is set on mount — before that fetch lands — so it was
   * always thrown away a moment after arriving, and asking the assistant to
   * expand a day into a week switched to this screen and showed nothing.
   */
  const lastCityId = useRef<string | null>(null);
  useEffect(() => {
    if (!city) return;
    api.routeStyles(city.id).then(setStyles).catch(() => setStyles([]));
    api.cityCategories(city.id).then(setCategories).catch(() => setCategories([]));

    const previous = lastCityId.current;
    lastCityId.current = city.id;
    // Arriving at the first city is not a change; nothing on the page refers
    // to a city yet, so there is nothing to invalidate.
    if (previous === null || previous === city.id) return;

    // A city change invalidates every place reference in the plan.
    setTrip(null);
    setRequest(null);
    setSavedId(null);
    setBase(null);
    setFinish(null);
    setMustVisit([]);
    setAvoid([]);
    setAvoidCategories([]);
    setAvoidCategoryPoints([]);
    setAvoidDistanceByPoiId({});
    setAvoidDistanceByCategoryKey({});
    setShownCategories([]);
  }, [city]);

  /* Shared with the planner so a category is the same colour on both screens. */
  const categoryColours = useMemo(
    () => buildCategoryColours(categories.map((c) => c.key)),
    [categories],
  );

  /*
    The dots for the ticked kinds, over the ground in view.

    The whole city was nearly two megabytes of GeoJSON with every kind
    ticked, and the limit holding that down cuts the list by name — so
    the map drew everything from A to M and nothing after. See the single
    day screen, which asks the same way.
  */
  useEffect(() => {
    if (!city || shownCategories.length === 0) { setPoiData(null); return; }
    api
      .poisGeoJson({
        cityId: city.id,
        categoryKeys: shownCategories,
        limit: 4000,
        ...(poiBox ? { bbox: poiBox } : {}),
      })
      .then(setPoiData)
      .catch(() => setPoiData(null));
  }, [city, shownCategories, poiBox]);

  /*
   * An avoided category still has a radius, so its zones must be drawn like
   * any other — otherwise "avoid all police stations at 500 m" shows nothing
   * on the map until the trip is planned, and there is no way to judge how
   * much of the city it swallows.
   */
  useEffect(() => {
    if (!city || avoidCategories.length === 0) {
      setAvoidCategoryPoints([]);
      return;
    }
    let cancelled = false;
    api
      .poisGeoJson({ cityId: city.id, categoryKeys: avoidCategories, limit: 300 })
      .then((data) => {
        if (cancelled) return;
        const fc = data as {
          features?: {
            geometry: { coordinates: [number, number] };
            properties?: { categoryKey?: string };
          }[];
        };
        setAvoidCategoryPoints((fc.features ?? []).map((f) => ({
          lng: f.geometry.coordinates[0],
          lat: f.geometry.coordinates[1],
          categoryKey: String(f.properties?.categoryKey ?? ''),
        })));
      })
      .catch(() => { if (!cancelled) setAvoidCategoryPoints([]); });
    return () => { cancelled = true; };
  }, [city, avoidCategories]);

  /*
   * Resolved exactly as the server resolves it: the place's own radius, then
   * its category's, then the slider. Drawn any other way the preview would
   * disagree with the plan it is previewing.
   */
  const radiusFor = useCallback(
    (poiId: string | undefined, categoryKey: string | undefined) =>
      (poiId ? avoidDistanceByPoiId[poiId] : undefined)
      ?? (categoryKey ? avoidDistanceByCategoryKey[categoryKey] : undefined)
      ?? avoidDistanceM,
    [avoidDistanceByPoiId, avoidDistanceByCategoryKey, avoidDistanceM],
  );

  /**
   * The circles the map draws before a route exists.
   *
   * Server-built buffers only arrive with a plan, so an avoided place chosen
   * now would otherwise show nothing at all until the trip is built.
   */
  const previewAvoid = useMemo(
    () => [
      ...avoid.map((p) => ({
        lat: p.lat, lng: p.lng, radiusM: radiusFor(p.id, p.categoryKey),
        categoryKey: p.categoryKey,
      })),
      ...avoidCategoryPoints.map((p) => ({
        ...p, radiusM: radiusFor(undefined, p.categoryKey),
      })),
      /*
        Areas marked on the map, drawn with their circle straight away.

        Read from the request rather than waiting for the day to come back,
        so marking one shows the ground it actually covers while the re-plan
        is still running — which is the moment the radius matters, since it
        is what the route is about to be pushed around.

        The operation's and this day's both, because a day that states its
        own avoidance replaces the operation's rather than adding to it.
      */
      ...[
        ...(request?.defaults.avoid?.points ?? []),
        ...(request?.days[selected]?.avoid?.points ?? []),
        // The page's own only while there is no request holding them, or
        // every marked area would be circled twice.
        ...(request ? [] : avoidPointPins),
      ].map((p) => ({ lat: p.lat, lng: p.lng, radiusM: avoidDistanceM })),
    ],
    [avoid, avoidCategoryPoints, radiusFor, request, selected, avoidDistanceM, avoidPointPins],
  );

  /** One place's or one category's own distance, or back to the slider. */
  const setAvoidDistanceOverride = useCallback(
    (scope: 'poi' | 'category', key: string, metres: number | null) => {
      const apply = (current: Record<string, number>) => {
        const next = { ...current };
        if (metres === null) delete next[key];
        else next[key] = metres;
        return next;
      };
      if (scope === 'poi') setAvoidDistanceByPoiId(apply);
      else setAvoidDistanceByCategoryKey(apply);
    },
    [],
  );

  const plan = async (next: TripRequest) => {
    setPlanning(true);
    setError(null);
    setStale([]);
    try {
      const result = await api.planTrip(next);
      setRequest(next);
      setTrip(result);
      // Whatever the map held when this was planned is what it now matches.
      plannedFrom.current = mapSignature;
      // And each day is now exactly what its card said, so an edit made in
      // the form from here on is an edit the drawn day does not have.
      daysPlannedFrom.current = draftDays.map((d) => d.signature);
      // Planned here, so it is this screen's to change again.
      setOpenedFromSaved(false);
      setEditing(false);
      setSelected(result.days.findIndex((d) => d.plan) >= 0
        ? result.days.findIndex((d) => d.plan)
        : 0);
      setSavedId(null);
    } catch (err) {
      setError((err as ApiError).message);
    } finally {
      setPlanning(false);
    }
  };

  /**
   * Re-plans one day, leaving the rest alone.
   *
   * The whole reason a trip is a list of independent plans. The other days'
   * stops go with the request so this one still avoids repeating them.
   */
  const replanDay = async (index: number, override?: TripRequest) => {
    const source = override ?? request;
    if (!source || !trip) return;
    setBusyDay(index);
    setError(null);
    try {
      const others = trip.days
        .filter((d) => d.index !== index)
        .flatMap((d) => (d.plan?.stops ?? [])
          .map((s) => s.poiId)
          .filter((id): id is string => Boolean(id)));

      const { day, stale: nowStale } = await api.planTripDay(source, index, others);
      setTrip((current) => current && {
        ...current,
        days: current.days.map((d) => (d.index === index ? day : d)),
      });
      setStale((current) => [...new Set([...current, ...nowStale])]);
    } catch (err) {
      setError((err as ApiError).message);
    } finally {
      setBusyDay(null);
    }
  };

  /** Rewrites one day of the request and re-plans that day alone. */
  const editDay = useCallback(
    async (index: number, patch: (day: TripRequest['days'][number]) => TripRequest['days'][number],
           message: string) => {
      if (!request || !trip) return;
      const next: TripRequest = {
        ...request,
        days: request.days.map((d, i) => (i === index ? patch(d) : d)),
      };
      setRequest(next);
      setToast(message);
      await replanDay(index, next);
    },
    // replanDay is not a callback and closes over `request` and `trip`; both
    // are listed, so the patched request never plans against a stale trip.
    [request, trip],
  );

  /**
   * Turns a map click into a place in one of the trip's lists.
   *
   * A stop lands on the day being shown: the map shows one day at a time, and
   * that is the day somebody is looking at when they click. Avoidance is the
   * whole trip's — a place worth keeping clear of on Tuesday is worth keeping
   * clear of on Wednesday, which is how the trip form treats it too.
   *
   * The place also joins the page's own list so the form shows it and it
   * survives. That list belongs to the trip rather than to a day, so a later
   * re-plan from the form puts it on the first day; the day's own copy is
   * what makes the click take effect where it was made.
   */
  /**
   * Takes a place into the operation's stops.
   *
   * Shared by the map, which picks a dot and then asks what it should be,
   * and by the place browser, which decides first and hands a place over.
   * One rule about the ends either way: the base and the objective are
   * where a day begins and finishes, so neither is somewhere it also stops.
   *
   * The stop lands on the day being shown, for the same reason a map click
   * does — that is the day somebody is looking at — and also joins the
   * page's own list, which belongs to the operation rather than to a day.
   */
  const addStop = useCallback(
    async (poi: Poi) => {
      const reserved = base?.id === poi.id
        ? 'That is the base the days set off from'
        : objective?.id === poi.id ? 'That is where the days finish' : '';
      if (reserved) {
        setToast(reserved);

        return;
      }

      setMustVisit((current) =>
        current.some((p) => p.id === poi.id) ? current : [...current, poi]);

      if (request && trip) {
        await editDay(
          selected,
          (d) => ({
            ...d,
            mustVisitPoiIds: [...new Set([...(d.mustVisitPoiIds ?? []), poi.id])],
          }),
          `${poi.name} added to day ${selected + 1} — updating it`,
        );
      } else {
        setToast(`${poi.name} added to places to visit`);
      }
    },
    [base?.id, objective?.id, request, trip, selected, editDay],
  );

  const assignRole = useCallback(
    async (role: 'visit' | 'avoid') => {
      if (!picked) return;
      const chosen = picked;
      setPicked(null);
      try {
        // The dialog carries only what the map needed to draw the dot.
        const poi = await api.poi(chosen.id);
        if (role === 'visit') {
          await addStop(poi);
        } else {
          setAvoid((current) =>
            current.some((p) => p.id === poi.id) ? current : [...current, poi]);
          if (request && trip) {
            /*
             * Every day has to keep clear of it, but only the one on screen is
             * re-planned. The rest are marked instead — re-running five days
             * because somebody avoided one building is not a decision to make
             * on their behalf, and it is the same choice the strip already
             * makes for a moved endpoint.
             */
            const next: TripRequest = {
              ...request,
              defaults: {
                ...request.defaults,
                avoid: {
                  ...request.defaults.avoid,
                  poiIds: [...new Set([...(request.defaults.avoid?.poiIds ?? []), poi.id])],
                  distanceM: request.defaults.avoid?.distanceM ?? avoidDistanceM,
                },
              },
            };
            setRequest(next);
            setStale((current) => [...new Set([
              ...current,
              ...trip.days.filter((d) => d.plan && d.index !== selected).map((d) => d.index),
            ])]);
            setToast(`Avoiding ${poi.name} — updating day ${selected + 1}`);
            await replanDay(selected, next);
          } else {
            setToast(`Avoiding ${poi.name}`);
          }
        }
      } catch (err) {
        setToast((err as ApiError).message);
      }
    },
    [picked, request, trip, selected, avoidDistanceM, editDay, addStop],
  );

  /*
   * ── shaping a day's path by hand ────────────────────────────────
   *
   * Right-clicking the map offers a point the route is threaded through,
   * exactly as on the single-day screen. Per day, because the points are
   * indexed against a day's own legs: "after stop 3" means nothing on a day
   * that has two.
   */
  const viaPoints = useMemo(
    () => request?.days[selected]?.viaPoints ?? [],
    [request, selected],
  );

  /*
    A marked spot, given its job on the day being shown.

    Every role is written into that day of the request and re-planned, which
    is how `assignRole` already works for a place clicked on the map — so a
    spot and a place behave the same once chosen.

    The avoided ones are the exception, and deliberately: a day's `avoid`
    REPLACES the trip's rather than adding to it, so writing one day's marked
    area there would quietly drop everything the operation avoids for that
    day. It goes on the trip instead, which also matches how the place picker
    treats avoidance.
  */
  /*
    Everything the operation holds in common, changed at once.

    Only the day on screen is re-planned; the rest are marked stale. Re-running
    a week because somebody moved one marker is not a decision to make on
    their behalf, and it is the choice `assignRole` already makes.
  */
  /*
   * Writes an edit into the request and stops there.
   *
   * `editDay` and `editOperation` below re-plan as part of the edit, which
   * is right for a gesture that IS the change — dragging an endpoint, moving
   * a shaping point. Marking a spot from the map is not that: on the
   * single-day screen a mark is recorded and the round is left alone until
   * somebody asks for it, and the day's own "out of date, re-plan?" notice
   * is what asks. Doing both meant the day re-planned itself and then
   * offered to re-plan again.
   */
  const patchDay = useCallback(
    (index: number, patch: (day: TripRequest['days'][number]) => TripRequest['days'][number],
     message: string) => {
      if (!request) return;
      setRequest({
        ...request,
        days: request.days.map((d, i) => (i === index ? patch(d) : d)),
      });
      setToast(message);
    },
    [request],
  );

  /** The same, for an edit that belongs to every day. */
  const patchOperation = useCallback(
    (patch: (r: TripRequest) => TripRequest, message: string) => {
      if (!request) {
        setToast('Plan the operation first');

        return;
      }
      setRequest(patch(request));
      setToast(message);
    },
    [request],
  );

  const markSpot = useCallback(
    /*
      Nothing here waits on anything any more: a mark is recorded and the
      round is left as it is, exactly as on the single-day screen.
    */
    (role: 'start' | 'end' | 'visit' | 'avoid') => {
      if (!markedPoint) return;
      const point = markedPoint;
      const label = `${point.lat.toFixed(5)}, ${point.lng.toFixed(5)}`;
      const where = { kind: 'coords' as const, lat: point.lat, lng: point.lng, label };
      const spot = { lat: point.lat, lng: point.lng, label };
      setMarkedPoint(null);

      const day = selected + 1;

      /*
        Recorded here FIRST, whatever else happens to it.

        There are two stores and they have to agree: the page's own lists,
        which the form folds into every request it builds, and the request
        itself, which is edited in place and re-planned when one already
        exists. Writing only to the request looked right — the stop appeared
        and the day re-planned — but the form knew nothing about it, so the
        next change made in the form rebuilt the request from what the form
        held and the marks were gone. That is the "map not synced with the
        form" above: they were never lost on the map, they were lost on the
        next plan.

        A day's own start and finish are the one thing not kept this way: a
        day draft has no room for a coordinate, so marking one after planning
        still only lives in the request. It is listed as a gap rather than
        half-solved here.
      */
      if (role === 'visit') {
        setDayStopPins((current) => ({
          ...current, [selected]: [...(current[selected] ?? []), spot],
        }));
      } else if (role === 'avoid' && scope === 'operation') {
        setAvoidPointPins((current) => [...current, spot]);
      } else if (role === 'start' || role === 'end') {
        if (scope === 'operation') {
          if (role === 'start') setBasePin(spot); else setFinishPin(spot);
        } else {
          // This day's own end, which had nowhere to live at all: the toast
          // said it was set and nothing stored it.
          setDayEndPins((current) => ({
            ...current, [selected]: { ...current[selected], [role]: spot },
          }));
        }
      }

      /*
        Nothing planned yet, so there is no request to edit and no day to
        re-plan — the lists above are the whole of it. This is the point of
        marking before planning: the map is a better way to say "start here,
        avoid that" than four search boxes.
      */
      if (!request || !trip) {
        if (role === 'start') {
          setToast(scope === 'operation'
            ? 'Every day will start here'
            : `Day ${day} will start here`);
        } else if (role === 'end') {
          setToast(scope === 'operation'
            ? 'Every day will finish here'
            : `Day ${day} will finish here`);
        } else if (role === 'visit') {
          setToast(`Stop added to day ${day}`);
        } else {
          setToast('Avoiding the marked area on every day');
        }

        return;
      }

      if (scope === 'operation') {
        if (role === 'start') {
          patchOperation(
            (r) => ({ ...r, defaults: { ...r.defaults, start: where } }),
            'Every day now starts at the marked spot',
          );
        } else if (role === 'end') {
          patchOperation(
            (r) => ({ ...r, defaults: { ...r.defaults, end: where } }),
            'Every day now ends at the marked spot',
          );
        } else {
          patchOperation((r) => ({
            ...r,
            defaults: {
              ...r.defaults,
              avoid: {
                ...r.defaults.avoid,
                poiIds: r.defaults.avoid?.poiIds ?? [],
                points: [...(r.defaults.avoid?.points ?? []), spot],
                distanceM: r.defaults.avoid?.distanceM ?? avoidDistanceM,
              },
            },
          }), 'Avoiding the marked area on every day');
        }

        return;
      }

      if (role === 'start') {
        patchDay(selected, (d) => ({ ...d, start: where }),
          `Day ${day} now starts at the marked spot`);
      } else if (role === 'end') {
        patchDay(selected, (d) => ({ ...d, end: where }),
          `Day ${day} now ends at the marked spot`);
      } else if (role === 'visit') {
        patchDay(selected, (d) => ({
          ...d, mustVisitPoints: [...(d.mustVisitPoints ?? []), spot],
        }), `Stop added to day ${day}`);
      } else {
        /*
          A day's avoidance REPLACES the operation's rather than adding to it,
          so the operation's has to be carried over with it. Without that,
          avoiding one area on one day quietly released everything that day
          was keeping clear of.
        */
        patchDay(selected, (d) => {
          const base = d.avoid ?? {
            poiIds: request?.defaults.avoid?.poiIds ?? [],
            categoryKeys: request?.defaults.avoid?.categoryKeys ?? [],
            points: request?.defaults.avoid?.points ?? [],
            distanceM: request?.defaults.avoid?.distanceM ?? avoidDistanceM,
          };

          return {
            ...d,
            avoid: { ...base, points: [...(base.points ?? []), spot] },
          };
        }, `Avoiding the marked area on day ${day}`);
      }
    },
    [markedPoint, scope, selected, patchDay, patchOperation, request, trip, avoidDistanceM],
  );

  /*
    Bend this day's path through the marked spot.

    Which leg to bend is measured rather than asked — the nearest to the
    spot — exactly as the one-day planner does it.
  */
  const routeThroughSpot = useCallback(() => {
    const plan = trip?.days[selected]?.plan;
    if (!markedPoint || !plan) return;
    const point = markedPoint;
    setMarkedPoint(null);

    let nearestLeg = 0;
    let nearest = Infinity;
    plan.legs.forEach((leg, index) => {
      for (const [lng, lat] of leg.geometry) {
        // Squared degrees, longitude narrowed by latitude. Ranking only.
        const dx = (lng - point.lng) * Math.cos((point.lat * Math.PI) / 180);
        const dy = lat - point.lat;
        const d = dx * dx + dy * dy;
        if (d < nearest) { nearest = d; nearestLeg = index; }
      }
    });

    void editDay(selected, (d) => ({
      ...d,
      viaPoints: [...(d.viaPoints ?? []), { ...point, afterStopIndex: nearestLeg }],
    }), `Day ${selected + 1} routed through the marked spot`);
  }, [markedPoint, trip, selected, editDay]);

  /*
   * The day's line, redrawn by hand.
   *
   * Written into that day of the request and re-planned, exactly as removing
   * a via point is — so bending and straightening are one pair of gestures
   * rather than two mechanisms. The leg comes from where the handle was
   * picked up, which is the only place that knows it once the cursor has
   * left the line.
   */
  const bendDay = useCallback(
    (point: { lat: number; lng: number }, afterStopIndex: number) => {
      void editDay(
        selected,
        (d) => ({
          ...d,
          viaPoints: [...(d.viaPoints ?? []), { ...point, afterStopIndex }],
        }),
        'Route redrawn through the point',
      );
    },
    [editDay, selected],
  );

  /*
   * An end of the day, dragged to a new place on the map.
   *
   * The same gesture the single-day screen has had all along, and it was
   * simply never wired here: the S and E of a planned day sat there looking
   * exactly like the draggable ones and did nothing.
   *
   * Written onto THIS day rather than the operation, because that is what
   * dragging the marker in front of you means. A day that was taking the
   * operation's base now has its own, which the day card already shows as
   * an override and already offers to give back.
   */
  const dragDayEnd = useCallback(
    (kind: 'start' | 'end', point: { lat: number; lng: number }) => {
      const label = `${point.lat.toFixed(5)}, ${point.lng.toFixed(5)}`;

      void editDay(
        selected,
        (d) => {
          const moved = { kind: 'coords' as const, ...point, label };
          if (kind === 'end') return { ...d, end: moved };

          /*
            Dragging the START moves the start, and nothing else.

            A day with no end of its own finishes wherever it began — that
            is what "every day returns to the base" means on the wire, an
            absent `end`. So moving the start used to move the finish with
            it: both markers jumped to the drop and the base dropped out of
            the day altogether, which is not what dragging one marker says.

            Where the finish was implicit it is written down at its old
            position first, so the day now runs from the new start back to
            where it was always ending. A day that already named its own
            finish is untouched.
          */
          const stays = d.end
            ?? request?.defaults.end
            ?? d.start
            ?? request?.defaults.start;

          return {
            ...d,
            start: moved,
            ...(d.end || !stays ? {} : { end: stays }),
          };
        },
        kind === 'start' ? 'Day starts here now' : 'Day finishes here now',
      );
    },
    [editDay, selected, request],
  );

  /* A shaping point of the day, moved. See the one-day form. */
  const moveViaPoint = useCallback(
    (index: number, point: { lat: number; lng: number }) => {
      void editDay(
        selected,
        (d) => ({
          ...d,
          viaPoints: (d.viaPoints ?? []).map(
            (via, i) => (i === index ? { ...via, ...point } : via),
          ),
        }),
        'Route redrawn',
      );
    },
    [editDay, selected],
  );

  const handleViaPointClick = useCallback(
    (index: number) => {
      void editDay(
        selected,
        (d) => ({ ...d, viaPoints: (d.viaPoints ?? []).filter((_, i) => i !== index) }),
        'Via point removed',
      );
    },
    [editDay, selected],
  );

  /*
   * Valhalla caps the TOTAL circumference of every exclusion polygon, so the
   * number of places that fit falls as the radius grows. The same arithmetic
   * the single-day screen shows, so the two never disagree.
   */
  const current = trip?.days[selected] ?? null;

  /*
   * What a turn changed, applied to the day being looked at.
   *
   * The assistant speaks in single days, because a day is what it plans; the
   * map here shows one day at a time, and that is the one somebody is looking
   * at when they ask. So its answer lands on the selected day, through the
   * same path an edit from the form takes — which means it is re-planned and
   * the trip stays consistent, rather than one day being patched in place
   * while the rest of the week believes something else.
   */
  const applyToSelectedDay = useCallback(
    /*
     * The plan the turn produced is not used: applying the draft re-plans the
     * day through the same path a form edit takes, and a plan handed straight
     * in would be one the trip never computed — right for a single day and
     * wrong for a week, where the days after it inherit where this one ends.
     */
    (draft: Partial<PlanRequest>, plan: RoutePlan | null) => {
      /*
       * A day planned with no trip to put it on goes to the single-day
       * screen, which is the mirror of a week asked for on that screen
       * coming here. It used to be dropped in silence: the assistant said it
       * had planned the day, and this page — having no trip to attach it to —
       * did nothing at all with it.
       *
       * Only when a route was actually DRAWN. Most turns come back with a
       * partial draft and no plan — a city named, an hour changed — and
       * switching planner on one of those would throw an operator off the
       * screen they are working on mid-sentence.
       */
      if (!hasTrip.current) {
        if (!plan) return;
        void navigate('/plan', { state: { draft, plan, assistantOpen: true } });
        return;
      }
      if (!request || !trip) return;
      void editDay(
        selected,
        (day) => ({ ...day, ...draft }),
        'The assistant changed this day',
      );
    },
    [editDay, request, trip, selected, navigate],
  );

  /*
   * A whole trip, when that is what was asked for.
   *
   * On the planner this navigates here; here it is already the right screen,
   * so the trip simply replaces the one on it. The request comes back too,
   * because the page re-plans from a request and would otherwise hold a trip
   * it could not edit.
   */
  const replaceTrip = useCallback((tripRequest: unknown, planned: unknown) => {
    const next = tripRequest as TripRequest;
    setRequest(next);
    setTrip(planned as TripPlan);
    setFormSeed(next);
    // These days are now exactly what was planned, whatever was stale before.
    daysPlannedFrom.current = draftDays.map((d) => d.signature);
    setOpenedFromSaved(false);
    hasTrip.current = true;
    setSelected(0);
    // There are days to mark now, and one of them is on screen.
    setMarkScope('day');
    setEditing(false);
    // Filled in as well as drawn, for the reason given where it is defined:
    // a trip whose form is empty re-plans without any of it.
    void adoptTripRequest(next);
    setToast('The assistant planned this trip');
  }, [adoptTripRequest]);

  // Stop 3 of Tuesday is not stop 3 of Wednesday.
  useEffect(() => { setHighlighted(null); }, [selected, trip]);

  /**
   * What the map draws before anything is planned.
   *
   * The base as the start marker, chosen places as stops, avoided places as
   * their own — the same vocabulary the single-day screen uses, so a marker
   * means the same thing on both. Dropped once the selected day is on the
   * map, whose numbered circles already stand for the same places.
   */
  const mapPins = useMemo(() => {
    const pins: {
      lat: number; lng: number; label?: string; address?: string;
      fromList?: boolean; role: 'start' | 'end' | 'visit' | 'avoid' | 'suggested';
      highlighted?: boolean;
      onMove?: (point: { lat: number; lng: number }) => void;
      onRemove?: () => void;
    }[] = [];

    const add = (poi: Poi, role: 'start' | 'end' | 'visit' | 'avoid') => pins.push({
      lat: poi.lat, lng: poi.lng, label: poi.name,
      ...(poi.address ? { address: poi.address } : {}),
      role, fromList: true,
    });

    /*
     * What THIS day says about its own ends, read before anything else.
     *
     * A day may depart from or finish somewhere other than the operation
     * does - by right-click on the map, or from its own card - and when it
     * does, the operation's base and objective are not where this day
     * starts and stops. Drawn anyway, they put a second S or E on the map
     * claiming otherwise: the screen showed two red E circles, one where the
     * day actually ended and one at the operation's objective, with nothing
     * to say which was which.
     */
    const dayPlanned = request?.days[selected];
    const dayDraft = draftDays[selected];
    const ownStart = Boolean(request ? dayPlanned?.start : dayDraft?.start);
    const ownEnd = Boolean(request ? dayPlanned?.end : dayDraft?.end);

    if (base && !ownStart) add(base, 'start');
    // Where the days finish, when that is not the base they left from. Drawn
    // before anything is planned, as the base always was.
    if (objective && !ownEnd) add(objective, 'end');

    /*
     * Marked on the map before anything was planned. Each is drawn in the
     * role it was given, so the map answers "what have I said so far?"
     * without the form having to be read.
     */
    const marked = (pt: CoordStop, role: 'start' | 'end' | 'visit' | 'avoid') => pins.push({
      lat: pt.lat, lng: pt.lng, ...(pt.label ? { label: pt.label } : {}), role,
    });

    for (const poi of mustVisit) add(poi, 'visit');
    for (const poi of avoid) add(poi, 'avoid');

    /*
     * Spots marked by right-click, which have no place record to come from.
     *
     * Drawn from the request rather than from the plan, so one is visible the
     * moment it is marked instead of only after the day comes back — and so
     * it stays visible if the day cannot use it. The stops are this day's;
     * the avoided areas are the operation's, which is where they are kept.
     */
    /*
     * The request's copy where there is one, the page's otherwise — never
     * both. The two hold the same marks on purpose (see `markSpot`), so
     * reading them both drew every marked spot twice on the same pixel.
     */
    const day = dayPlanned;
    const markedStops = request ? day?.mustVisitPoints ?? [] : dayStopPins[selected] ?? [];
    /*
      Marked spots carry their own way off the map.

      They were placed by right-click and live nowhere else, so without this
      a spot dropped by mistake stayed on the day for good: the form shows
      places, not coordinates, and there was nothing to click.
    */
    markedStops.forEach((pt, i) => {
      pins.push({
        lat: pt.lat, lng: pt.lng, ...(pt.label ? { label: pt.label } : {}), role: 'visit',
        onMove: (at: { lat: number; lng: number }) => {
          void editDay(
            selected,
            (d) => ({
              ...d,
              mustVisitPoints: (d.mustVisitPoints ?? []).map(
                (p, n) => (n === i
                  ? { ...p, ...at, label: `${at.lat.toFixed(5)}, ${at.lng.toFixed(5)}` }
                  : p),
              ),
            }),
            'Marked stop moved',
          );
        },
        onRemove: () => {
          void editDay(
            selected,
            (d) => ({
              ...d,
              mustVisitPoints: (d.mustVisitPoints ?? []).filter((_, n) => n !== i),
            }),
            'Marked stop removed',
          );
        },
      });
    });
    for (const pt of request
      ? [...(request.defaults.avoid?.points ?? []), ...(day?.avoid?.points ?? [])]
      : avoidPointPins) {
      pins.push({
        lat: pt.lat, lng: pt.lng, ...(pt.label ? { label: pt.label } : {}), role: 'avoid',
      });
    }

    /*
     * A marked start or destination, shown before the day comes back.
     *
     * The S and E circles are drawn from the PLAN, so marking one left the
     * map showing yesterday's endpoints for as long as the re-plan took —
     * on a day with exclusions, half a minute of the map disagreeing with
     * what was just asked for. This day's own if it has one, else the
     * operation's, which is the same order the planner resolves them in.
     */
    if (request) {
      const ends = [
        ['start', day?.start ?? request.defaults.start],
        ['end', day?.end ?? request.defaults.end],
      ] as const;
      for (const [role, ref] of ends) {
        if (ref?.kind !== 'coords') continue;
        // Only this DAY's own end can be cleared from here. The operation's
        // belongs to the form above, where every day reads it.
        const own = role === 'start' ? dayPlanned?.start : dayPlanned?.end;
        pins.push({
          lat: ref.lat, lng: ref.lng, ...(ref.label ? { label: ref.label } : {}), role,
          ...(own
            ? {
              onMove: (at: { lat: number; lng: number }) => dragDayEnd(role, at),
              onRemove: () => {
                void editDay(
                  selected,
                  (d) => ({ ...d, [role]: undefined }),
                  role === 'start' ? 'Start removed' : 'Destination removed',
                );
              },
            }
            : {}),
        });
      }
    } else {
      /*
       * Before a plan, from the days the form is holding.
       *
       * This day's own ends where it has them, and only then the
       * operation's — a day that finished somewhere else still had the
       * operation's E drawn for it, so the map and the day's own card
       * disagreed about where it ends.
       */
      const own = dayDraft;
      if (own?.start) marked(own.start, 'start');
      else if (basePin) marked(basePin, 'start');
      if (own?.end) marked(own.end, 'end');
      else if (objectivePin) marked(objectivePin, 'end');
    }

    /*
     * Suggestions last, so one never draws over a place already chosen —
     * including itself, once added.
     */
    for (const place of suggestionPreview.places) {
      if (mustVisit.some((p) => p.id === place.poiId)) continue;
      pins.push({
        lat: place.lat, lng: place.lng, label: place.name,
        role: 'suggested', fromList: true,
        highlighted: suggestionPreview.hoveredPoiId === place.poiId,
      });
    }

    const stops = current?.plan?.stops ?? [];
    return pins.filter((pin) => !stops.some(
      (stop) => Math.abs(stop.lat - pin.lat) < 1e-5 && Math.abs(stop.lng - pin.lng) < 1e-5,
    ));
  }, [
    base, objective, mustVisit, avoid, suggestionPreview, current, request, selected,
    basePin, objectivePin, dayStopPins, avoidPointPins, draftDays,
    // The pins carry their own removal and their own move now, and both
    // write to the day.
    editDay, dragDayEnd,
  ]);

  /*
   * Every other day's geometry, for the faded lines behind the selected one.
   *
   * Legs rather than a single line per day: a day's legs are already
   * separate, and joining them here would only have to be undone by the map.
   */
  /*
   * Every planned day, as the export wants them.
   *
   * A day that could not be planned has no route to write out, so it is left
   * out rather than printed as an empty page. The request each day was
   * planned from is rebuilt here because an operation keeps defaults and
   * overrides rather than one request per day.
   */
  const exportDays = useMemo(() => {
    if (!request || !trip) return [];

    return trip.days.flatMap((d, i) => {
      if (!d.plan) return [];
      const dayReq = dayPlanRequest(request, i, d.plan);

      return dayReq
        ? [{
            label: dayLabel(i, d.date),
            request: dayReq,
            plan: d.plan,
            ...(d.date ? { date: d.date } : {}),
          }]
        : [];
    });
  }, [request, trip]);

  const otherDays = useMemo(
    () => (trip?.days ?? [])
      .filter((d) => d.plan && d.index !== selected)
      .map((d) => ({
        index: d.index,
        colour: colourForDay(d.index),
        geometry: d.plan!.legs.map((leg) => leg.geometry),
      })),
    [trip, selected],
  );

  const save = async () => {
    if (!request || !trip || !city) return;
    try {
      const saved = savedId
        ? await api.updateTrip(savedId, { name: saveName, request, plan: trip })
        : await api.saveTrip({ cityId: city.id, name: saveName, request, plan: trip });
      setSavedId(saved.id);
      setSaveOpen(false);
    } catch (err) {
      setError((err as ApiError).message);
    }
  };

  if (!city) {
    return (
      <Stack sx={{ alignItems: 'center', py: 8 }}>
        {error ? <Alert severity="error">{error}</Alert> : <CircularProgress />}
      </Stack>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: '100%', minHeight: 0 }}>
      {/*
        The category browser, as a rail of its own — the same place and the
        same width the single-day screen puts it.
      */}
      {showCategories && (
        <Paper
          variant="outlined"
          sx={{
            width: 234,
            flexShrink: 0,
            borderRadius: 0,
            borderTop: 0, borderBottom: 0, borderLeft: 0,
            overflowY: 'auto',
            display: { xs: 'none', lg: 'block' },
          }}
        >
          <CategoryPanel
            categories={categories}
            selected={shownCategories}
            onChange={setShownCategories}
            colourFor={(key) => categoryColours.get(key) ?? '#888'}
          />
        </Paper>
      )}

      {/*
        ── the form and the itinerary ────────────────────────────
        Scrolls on its own rather than taking the page with it, so the map
        beside it stays where it is however long the form gets — which, with
        one card per day, it does.
      */}
      <Paper
        variant="outlined"
        sx={{
          width: { xs: '100%', md: 420 },
          flexShrink: 0,
          borderRadius: 0,
          borderTop: 0, borderBottom: 0, borderLeft: 0,
          overflowY: 'auto',
          minHeight: 0,
          p: 2.5,
          /*
             Folded when asked. The control that does it sits on the map, so
             it is there to press whether or not the assistant is open — which
             it was not when this column could only be folded from that
             panel's header.
          */
          display: {
            xs: trip && !editing && !panelOnMobile ? 'none' : 'block',
            md: panelOpen ? 'block' : 'none',
          },
        }}
      >
        <Stack spacing={2}>
          {/*
            City and the layers toggle, in the same row and the same order the
            planner puts them — so the two screens are learned once.
          */}
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <TextField
              select fullWidth size="small" label="City"
              value={city.id}
              onChange={(e) => setCity(cities?.find((c) => c.id === e.target.value) ?? null)}
            >
              {(cities ?? []).map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.name}
                  <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    {option.poiCount.toLocaleString('en-US')} places
                  </Typography>
                </MenuItem>
              ))}
            </TextField>
            <Tooltip title={showCategories ? 'Hide categories' : 'Show categories'}>
              <IconButton
                size="small"
                onClick={() => setShowCategories((v) => !v)}
                color={showCategories ? 'primary' : 'default'}
                sx={{ display: { xs: 'none', lg: 'inline-flex' } }}
              >
                <LayersOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
          <Divider />
          {error && (
            <Alert severity="error" variant="outlined" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/*
            Always mounted, hidden rather than removed once a trip exists.
            The form holds the whole trip — its dates, its days, everything
            each day overrides — and unmounting it threw all of that away, so
            "change the trip" had no way back to anything but a blank form.
            The planner solves it the same way, for the same reason.
          */}
          <Box sx={{ display: trip && !editing ? 'none' : 'block' }}>
            <TripForm
              initial={formSeed}
              city={city}
              styles={styles}
              categories={categories}
              maxDays={Number(settings['trip.max_days'] ?? 14)}
              replanSignal={replanSignal}
              planning={planning}
              onPlan={plan}
              onShowDetails={setDetailsPoiId}
              basemapUrl={basemapUrlFor(city, settings['map.shared_basemap'] === true)}
              colourFor={(key) => categoryColours.get(key) ?? '#888'}
              base={base}
              onBaseChange={setBase}
              finish={finish}
              onFinishChange={setFinish}
              onObjectiveChange={setObjective}
              onObjectivePinChange={setObjectivePin}
              onReturnToBaseChange={setReturnToBase}
              basePin={basePin}
              finishPin={finishPin}
              dayStopPins={dayStopPins}
              dayEndPins={dayEndPins}
              avoidPoints={avoidPointPins}
              onDaysChange={setDraftDays}
              onBasePinChange={setBasePin}
              onFinishPinChange={setFinishPin}
              onAvoidPointsChange={setAvoidPointPins}
              onDayStopPinsChange={(dayIndex, points) =>
                setDayStopPins((current) => ({ ...current, [dayIndex]: points }))}
              onDayEndPinChange={(dayIndex, role, pin) =>
                setDayEndPins((current) => {
                  const day = { ...current[dayIndex] };
                  if (pin) day[role] = pin;
                  else delete day[role];

                  return { ...current, [dayIndex]: day };
                })}
              mustVisit={mustVisit}
              onMustVisitChange={setMustVisit}
              onSuggestionPreview={setSuggestionPreview}
              onCoverageChange={setCoverage}
              avoid={avoid}
              onAvoidChange={setAvoid}
              avoidCategories={avoidCategories}
              onAvoidCategoriesChange={setAvoidCategories}
              avoidDistanceM={avoidDistanceM}
              onAvoidDistanceChange={setAvoidDistanceM}
              avoidDistanceByPoiId={avoidDistanceByPoiId}
              avoidDistanceByCategoryKey={avoidDistanceByCategoryKey}
              onAvoidDistanceOverride={setAvoidDistanceOverride}
            />
            {trip && editing && (
              <Button sx={{ mt: 1 }} onClick={() => setEditing(false)}>
                Back to the trip
              </Button>
            )}
          </Box>

          {/* Here only while the map is elsewhere. */}
          {trip && !editing && (
            <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 2 }}>
              <TripDayStrip
                days={stripDays}
                selected={selected}
                onSelect={setSelected}
                scope={scope}
                {...(trip ? {} : { onScopeChange: setMarkScope })}
                colourFor={colourForDay}
                stale={stale}
                busyIndex={busyDay}
              />
            </Box>
          )}

          {trip && current && !editing && (
            <Stack spacing={2}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack direction="row" spacing={2} useFlexGap sx={{ flexWrap: 'wrap' }}>
                  <Metric label="Days" value={`${trip.totals.plannedDays} of ${trip.totals.days}`} />
                  <Metric label="Stops" value={String(trip.totals.stops)} />
                  <Metric
                    label="Distance"
                    value={`${(trip.totals.distanceMeters / 1000).toFixed(0)} km`}
                  />
                </Stack>
                <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap' }} useFlexGap>
                  {/*
                    Not offered on one opened from Saved.

                    A saved operation is a record of what was done, and the
                    single-day screen treats one the same way: it shows the
                    route and offers no way into the form. Planning a new one
                    starts from the Plan tab. Everything else stays — the days
                    can still be looked through, and Save still updates the
                    row this was opened from.
                  */}
                  {!openedFromSaved && (
                    <Button
                      size="small" variant="outlined" startIcon={<EditOutlinedIcon />}
                      onClick={() => setEditing(true)}
                    >
                      Change trip
                    </Button>
                  )}
                  {/* Narrow screens only, where the column covers the map. */}
                  <Button
                    size="small"
                    color="inherit"
                    startIcon={<MapOutlinedIcon />}
                    onClick={() => setPanelOnMobile(false)}
                    sx={{ display: { xs: 'inline-flex', md: 'none' } }}
                  >
                    Map
                  </Button>
                  {/*
                    Saving goes with the screen that reads them back: a trip
                    saved where nothing lists it is written into a drawer the
                    build does not open.
                  */}
                  {FEATURES.savedTrips && (
                    <Button size="small" variant="outlined" startIcon={<SaveOutlinedIcon />}
                      onClick={() => { setSaveName(saveName || `${city.name} trip`); setSaveOpen(true); }}
                    >
                      {savedId ? 'Update saved trip' : 'Save trip'}
                    </Button>
                  )}
                  {/*
                    The whole operation, a day a page.

                    Every planned day goes in: an operator carrying a week
                    wants the week, and the map's plates are taken by walking
                    the map through the days and putting it back afterwards.
                  */}
                  {request && (
                    <ExportRouteButton
                      days={exportDays}
                      /* The name it was saved under if it has one, and
                         otherwise the ground it covers: base to objective. */
                      name={saveName.trim() || spanTitle(
                        exportDays[0]?.plan,
                        exportDays[exportDays.length - 1]?.plan,
                        `${city.name} operation`,
                      )}
                      getMap={() => exportMap.current}
                      onShowDay={setSelected}
                      activeDay={selected}
                    />
                  )}
                  {/*
                    No "Start again" here.

                    It threw away the plan while leaving the form holding the
                    trip that produced it, which is a half-reset nobody asked
                    for: "Change trip" already reopens the form with
                    everything in it, and a genuinely blank start is what
                    reloading the page is for.
                  */}
                </Stack>
              </Paper>

              {trip.warnings.map((w) => (
                <Alert key={w} severity="warning" variant="outlined">{w}</Alert>
              ))}

              {/* ── the selected day ─────────────────────────────── */}
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <Typography variant="h3" sx={{ flex: 1 }}>
                  Day {current.index + 1}
                </Typography>
                {stale.includes(current.index) && (
                  <Chip size="small" color="warning" variant="outlined" label="Out of date" />
                )}
                <Button
                  size="small"
                  startIcon={busyDay === current.index
                    ? <CircularProgress size={13} />
                    : <RefreshIcon fontSize="small" />}
                  disabled={busyDay !== null || current.skipped}
                  onClick={() => void replanDay(current.index)}
                >
                  Re-plan this day
                </Button>
              </Stack>

              {current.skipped && (
                <Alert severity="info" variant="outlined">
                  This day was left free.
                </Alert>
              )}
              {current.error && (
                <Alert severity="error" variant="outlined">{current.error}</Alert>
              )}

              {current.plan && (
                <ItineraryPanel
                  passedHidden={passedHidden}
                  basemapUrl={basemapUrlFor(city, settings['map.shared_basemap'] === true)}
                  {...(exportDays[selected]
                    ? {
                      directionsFor: directionsForPlan(
                        exportDays[selected].request, exportDays[selected].plan,
                      ),
                    }
                    : {})}
                  plan={current.plan}
                  city={city}
                  onShowDetails={setDetailsPoiId}
                  onShowPoint={(index) => { setHighlighted(index); setPinIndex(index); }}
                  highlightedStopIndex={highlighted}
                  onStopHover={setHighlighted}
                  onStopFocus={focusOnMap}
                  onStopReveal={focusOnMap}
                  {...(current.request.availableMinutes
                    ? { availableMinutes: current.request.availableMinutes }
                    : {})}
                  /* Once this operation has been saved it is in the record
                     itself, and every stop would report as covered by it. */
                  {...(savedId ? { coverageIgnoreId: savedId } : {})}
                  coverageDayNote={dayLabel(selected, current.date)}
                  {...(current.date ? { coverageRoundDate: current.date } : {})}
                />
              )}
            </Stack>
          )}
        </Stack>
      </Paper>

      {/* ── the map, with the day strip above it ────────────────── */}
      <Box
        sx={{
          flex: 1, minWidth: 0, minHeight: 0,
          display: {
            xs: trip && !editing && !panelOnMobile ? 'flex' : 'none',
            md: 'flex',
          },
          flexDirection: 'column',
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, position: 'relative' }}>
          {/*
            Says which day, because an operation re-plans one at a time and
            "calculating" with no subject leaves somebody watching the wrong
            card on the strip.
          */}
          <PlanningProgress
            open={planning || busyDay !== null}
            avoidCount={avoid.length + avoidCategories.length}
            what={busyDay !== null ? `day ${busyDay + 1}` : 'the days'}
          />
          {/*
            Said over the map, as on the single day, and for the same reason:
            the change that made this stale was made here, and the form is
            scrolled somewhere else or not on screen at all.
          */}
          {(staleTrip || daysMoved) && (
            <Alert
              severity="info"
              variant="filled"
              sx={{
                position: 'absolute',
                top: 12,
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 4,
                alignItems: 'center',
                boxShadow: 4,
                maxWidth: 'calc(100% - 24px)',
              }}
              action={(
                <Button
                  size="small"
                  color="inherit"
                  variant="outlined"
                  disabled={planning}
                  onClick={() => setReplanSignal((n) => n + 1)}
                >
                  {planning ? 'Planning…' : 'Plan again'}
                </Button>
              )}
            >
              {planning
                ? 'Your changes will need a new plan once this one finishes.'
                : 'Your changes are not on the map yet.'}
            </Alert>
          )}

          {/*
            The way back, on a narrow screen.

            The map replaces the column there rather than sitting beside it,
            which takes every control away with it — including the one that
            brings the column back. This is that one, and it exists only at
            that width because anywhere else the column is already on screen.
          */}
          {trip && !editing && (
            <Button
              variant="contained"
              size="small"
              startIcon={<ArrowBackIcon />}
              onClick={() => setPanelOnMobile(true)}
              sx={{
                display: { xs: 'inline-flex', md: 'none' },
                position: 'absolute',
                // Below the day strip, which owns the top of the map.
                top: 64,
                left: 12,
                zIndex: 6,
                boxShadow: 3,
              }}
            >
              Itinerary
            </Button>
          )}

          {/* The same control the single-day screen has, for the same reason. */}
          <MapOnlyToggle
            open={panelOpen}
            onToggle={() => setPanelOpen((open) => !open)}
            whatIsBeside="Itinerary"
          />
          {/*
            The day strip floats over the map rather than sitting above it.
            A row of cards that tall took a third of the map's height on a
            laptop, and the map is the thing being read — the strip only has
            to be reachable, not to have a shelf of its own. The wrapper
            ignores pointer events so the map stays draggable in the gaps
            between cards.

            Inset from the LEFT, which is where the map keeps its zoom and
            compass. It was inset from the right instead — a shelf of empty
            space reserved against nothing, while the first day's card sat
            squarely under the zoom buttons with its date unreadable.
          */}
          {stripDays.length > 0 && (
            <Box
              sx={{
                /*
                  Only where the map and the column share the screen. Below
                  that they take turns, and a strip reachable only from the
                  map would be reachable only from the view somebody switched
                  to in order to stop reading the day it selects — so on a
                  narrow screen it sits in the column instead.
                */
                display: { xs: 'none', md: 'block' },
                position: 'absolute',
                zIndex: 2,
                // Clear of the zoom on the left and the Map only button
                // on the right, both of which own their corner.
                top: 10, left: 52, right: 130,
                pointerEvents: 'none',
                '& > *': { pointerEvents: 'auto' },
              }}
            >
              <TripDayStrip
                days={stripDays}
                selected={selected}
                onSelect={setSelected}
                scope={scope}
                {...(trip ? {} : { onScopeChange: setMarkScope })}
                colourFor={colourForDay}
                stale={stale}
                busyIndex={busyDay}
              />
            </Box>
          )}
          <MapView
            passedHidden={passedHidden}
            onPassedHiddenChange={setPassedHidden}
            focusStop={focusStop}
            onMapReady={(map) => { exportMap.current = map; }}
            onCamera={(at) => watchView(at.bounds)}
            city={city}
            plan={current?.plan ?? null}
            basemapUrl={basemapUrlFor(city, settings['map.shared_basemap'] === true)}
            categoryColours={categoryColours}
            otherDays={otherDays}
            poiData={poiData}
            previewAvoid={previewAvoid}
            coverage={coverage}
            pins={mapPins}
            /*
             * Clicking a place offers it a role, and right-clicking a bare
             * spot marks it — both as on the single-day screen.
             *
             * The spot was left out on the grounds that a trip has no single
             * day for a dropped pin to belong to. It has: the map shows one
             * day at a time, and that is the day a place click already lands
             * on, so a marked spot lands there too.
             */
            onPoiClick={setPicked}
            onPinHint={() => setToast('Double-click a marked spot to remove it')}
            onMapClick={setMarkedPoint}
            /* Dragging the line bends the day on screen, and only that day. */
            onEndpointDrag={dragDayEnd}
            onRouteBend={bendDay}
            onViaPointMove={moveViaPoint}
            viaPoints={viaPoints}
            onViaPointClick={handleViaPointClick}
            onViaPointHint={() => setToast('Double-click a via point to remove it')}
            highlightedStopIndex={highlighted}
            /*
             * Clicking a numbered stop opens it and picks it out in the list,
             * exactly as on the single-day screen. Reaching a marker and then
             * hunting the same stop in the itinerary to read about it is work
             * the click can do itself.
             */
            onStopClick={(index) => {
              setHighlighted(index);
              const poiId = current?.plan?.stops[index]?.poiId;
              if (poiId) setDetailsPoiId(poiId);
            }}
          />

          {/*
            Over the map, bottom-right, exactly as on the single-day screen.
            Same component, same position, same button — somebody who has used
            one should not have to find the other.
          */}
          {/*
            The planning assistant, hidden in a production build.
          
            The same gate as the single-day planner. Multi-day mounts its
            own copy of the panel and its own button, so gating the other
            page left this one offering a feature the build is meant not to
            have. See src/config/features.ts.
          */}
          {city && FEATURES.planningAgent && assistantEnabled && (
            <Box
              /*
                Positioned exactly as on the single-day screen, down to the
                numbers — somebody moving between the two screens should find
                this where they left it rather than learning the page again.
                The planner's reasoning holds here unchanged:

                  * 10px is MapLibre's own control margin, so the button lines
                    up with the attribution below and the zoom above instead
                    of sitting a few pixels inside them.

                  * The bottom offset clears the map's bottom-right furniture.
                    MapLibre puts the credits there, and those are a licence
                    condition — credits under a button are not shown.
              */
              sx={{
                position: 'fixed', right: 10,
                bottom: { xs: 44, sm: 52 },
                zIndex: (theme) => theme.zIndex.drawer + 2,
                display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1,
                maxWidth: 'calc(100vw - 24px)',
              }}
            >
              {assistantOpen && (
                <Box sx={{ width: 'min(420px, calc(100vw - 32px))' }}>
                  <AssistantPanel
                    cityId={city.id}
                    cityName={city.name}
                    /*
                      The selected day, because that is the one on the map and
                      the one an operator means by "this". A trip with no day
                      planned yet has neither, and the assistant starts from
                      nothing — which is the same state it starts from on the
                      planner before a first route.
                    */
                    draft={current?.request ?? null}
                    plan={current?.plan ?? null}
                    onApply={applyToSelectedDay}
                    onTrip={replaceTrip}
                    formVisible={panelOpen}
                    onToggleForm={() => setPanelOpen((open) => !open)}
                    onClose={() => setAssistantOpen(false)}
                  />
                </Box>
              )}
              <Fab
                color="primary"
                variant={assistantOpen ? 'circular' : 'extended'}
                onClick={() => setAssistantOpen((open) => !open)}
              >
                {assistantOpen ? <CloseIcon /> : (
                  <>
                    <AutoAwesomeIcon sx={{ mr: 1 }} />
                    Plan by asking
                  </>
                )}
              </Fab>
            </Box>
          )}
        </Box>
      </Box>

      <Dialog open={saveOpen} onClose={() => setSaveOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{savedId ? 'Update this trip' : 'Save this trip'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus fullWidth size="small" label="Name" sx={{ mt: 1 }}
            value={saveName}
            onChange={(e) => setSaveName(e.target.value)}
            helperText="Saved to this browser only — there are no accounts"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!saveName.trim()} onClick={() => void save()}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <PlacePickerDialog
        picked={picked}
        onClose={() => setPicked(null)}
        onAssign={(role) => void assignRole(role)}
      />

      {/*
        What a marked spot is for — the one-day planner's dialog, asking the
        same five questions about the day on screen.

        Each offer appears only once it can be kept, as it does there: a stop
        or an exclusion needs a planned day to sit in, and bending the path
        needs a line to bend.
      */}
      <Dialog
        open={Boolean(markedPoint)}
        onClose={() => setMarkedPoint(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ pb: 0.5 }}>
          Mark this spot
          {/* Where it will land, decided on the strip rather than here. */}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {scope === 'operation'
              ? 'On every day — chosen on the strip'
              : `On day ${selected + 1}`}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {markedPoint && `${markedPoint.lat.toFixed(5)}, ${markedPoint.lng.toFixed(5)}`}
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Stack spacing={1}>
            <Button variant="contained" onClick={() => markSpot('start')}>
              {scope === 'operation' ? 'Start every day here' : `Start day ${selected + 1} here`}
            </Button>
            <Button variant="contained" onClick={() => markSpot('end')}>
              {scope === 'operation' ? 'End every day here' : `End day ${selected + 1} here`}
            </Button>
            {/*
              Offered whether or not anything has been planned.

              These were held back until a day existed to act on, which made
              the map useless for the thing it is best at: saying "go here,
              keep away from there" while the operation is still being built.
              Before a plan they are kept by the page and folded into the
              request; after one they are written into the day and re-planned.
            */}
            {/*
              A stop is one day's business.

              It was offered here too, landing on the first planned day — the
              rule the form's own operation-wide list follows. But a card that
              says "every day" and a button that says "the first day" are two
              different promises, and the one on the button is the one that
              would have been kept. Pick the day on the strip instead.
            */}
            {hasBothEnds && scope === 'day' && (
              <Button variant="outlined" onClick={() => markSpot('visit')}>
                Add as a stop on day {selected + 1}
              </Button>
            )}
            {/*
              Bending the path is a day's business, and needs a path: there is
              one line per day, none the operation holds in common, and
              nothing at all to bend until the day has been planned.
            */}
            {current?.plan && scope === 'day' && (
              <Button variant="outlined" onClick={routeThroughSpot}>
                Route through here
              </Button>
            )}
            {hasBothEnds && (
              <Button variant="outlined" color="warning" onClick={() => markSpot('avoid')}>
                {scope === 'operation' || !current?.plan
                  ? 'Avoid this area on every day'
                  : `Avoid this area on day ${selected + 1}`}
              </Button>
            )}
            {/* Said rather than left to be guessed at, since the two buttons
                simply are not there until the ends are. */}
            {!hasBothEnds && (
              <Typography variant="caption" color="text.secondary">
                Mark where the days start and finish first — a stop and an area
                to avoid are both about the way between them.
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMarkedPoint(null)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      <PinnedStopDialog
        stop={pinIndex === null ? null : current?.plan?.stops[pinIndex] ?? null}
        cityId={city?.id ?? null}
        onClose={() => setPinIndex(null)}
        onCopied={setToast}
      />

      <PlaceDetailsDialog
        poiId={detailsPoiId}
        onClose={() => setDetailsPoiId(null)}
        city={city}
        {...(city ? { basemapUrl: basemapUrlFor(city, settings['map.shared_basemap'] === true) } : {})}
        onAddToRound={(poiId) => {
          void api.poi(poiId).then(addStop).catch((err: ApiError) => setToast(err.message));
        }}
      />

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        message={toast ?? ''}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 700 }}>{value}</Typography>
    </Box>
  );
}
